/**
 * The one colour encoding on the map: how fresh a listing is.
 *
 * The ramp deliberately loses salience as it ages — a listing posted this
 * morning is a hot amber that pulls the eye across the whole viewport, while a
 * month-old one recedes into the same slate family as the coastline. In a market
 * where being first to call is the entire game, "new" earning the loudest colour
 * is the useful default, and it leaves every other hue free for UI meaning.
 *
 * Stops are upper-bound-exclusive in days, checked in order.
 */
export interface AgeStop {
  /** Matches listings strictly younger than this many days. */
  maxDays: number;
  color: string;
  label: string;
}

/**
 * Six bands, widening as they age.
 *
 * There used to be one stop per day, which produced fourteen colours that no eye
 * can hold apart: two adjacent stops differed by a few points of chroma, so the
 * ramp read as a smear and the only thing you could reliably tell from a pin was
 * roughly which third of it you were in. Fewer bands, each separated far enough
 * to be named at a glance, says more than a gradient nobody can decode.
 *
 * They widen because the resolution people need is not uniform. Today, yesterday
 * and the day before are three different prospects and each earns its own
 * colour. A seven-day-old and a nine-day-old listing are the same prospect, and
 * spending two colours on that distinction costs a colour somewhere it mattered.
 *
 * The last band is the cliff, and it lines up with the end of the age slider
 * (see AGE_STOPS) so no colour on the map is one the filter can't reach. The
 * slider is deliberately finer than this — it has stops inside these bands,
 * which is the normal relationship between a control and a key.
 *
 * Chosen for separation rather than for a smooth sweep: the closest two bands
 * here are ΔE 30 apart in Lab, where the old ramp's closest pair was 13 — about
 * the point at which two swatches stop being two swatches. Every stop still
 * carries a readable label; the tightest is 5.4:1 (see `ageInk`).
 */
export const AGE_SCALE: AgeStop[] = [
  { maxDays: 1, color: "#ffd60a", label: "today" },
  { maxDays: 2, color: "#ff8c1a", label: "1 d" },
  { maxDays: 4, color: "#ee5340", label: "2–3 d" },
  { maxDays: 7, color: "#a09892", label: "4–6 d" },
  { maxDays: 14, color: "#3f6579", label: "7–13 d" },
  { maxDays: Infinity, color: "#16212a", label: "14 d+" },
];

/**
 * The two inks a ramp colour can carry. `INK` is the same near-black the rest of
 * the map draws with; `CHALK` matches the cluster count.
 */
const INK = "#04070a";
const CHALK = "#e8f0f5";

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function luminance(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  return (
    0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
  );
}

const ratio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/**
 * Whichever ink reads on `color` — the label that sits *on* a ramp colour, in a
 * price pill or the card's age badge.
 *
 * Everything used to be drawn in INK, which was fine while the ramp was amber
 * and became unreadable the moment it wasn't: the oldest stop is a near-black,
 * and a near-black number on it is not dim, it is gone. Picking by contrast
 * rather than by a hand-kept flag means a future edit to a stop can't quietly
 * reintroduce that — the ink follows the colour.
 */
export function ageInk(color: string): string {
  const l = luminance(color);
  return ratio(l, luminance(INK)) >= ratio(l, luminance(CHALK)) ? INK : CHALK;
}

export function ageColor(days: number): string {
  for (const stop of AGE_SCALE) {
    if (days < stop.maxDays) return stop.color;
  }
  return AGE_SCALE[AGE_SCALE.length - 1].color;
}

/**
 * The same ramp as a MapLibre `step` expression, so the GPU does the
 * classification instead of the CPU re-tagging every feature when the clock
 * ticks over.
 *
 * `property` exists because clusters classify on `minAge` (the freshest listing
 * they contain) rather than their own `ageDays`. Both must come from here: when
 * the cluster layers carried their own hand-written copy of the stops, changing
 * the scale silently moved the dots and left the bubbles on the old colours.
 */
export function ageColorExpression(property = "ageDays"): unknown[] {
  return stepByAge((stop) => stop.color, property);
}

/** `ageInk` as a step expression — the price pill's `text-color`. */
export function ageInkExpression(property = "ageDays"): unknown[] {
  return stepByAge((stop) => ageInk(stop.color), property);
}

function stepByAge(pick: (stop: AgeStop) => string, property: string): unknown[] {
  const expr: unknown[] = ["step", ["get", property], pick(AGE_SCALE[0])];
  for (let i = 0; i < AGE_SCALE.length - 1; i++) {
    expr.push(AGE_SCALE[i].maxDays, pick(AGE_SCALE[i + 1]));
  }
  return expr;
}
